import { useState } from 'react'
import { FriendInfo } from '@/api/types'
import { useFriendActions } from '@/hooks/useFriendActions'
import { useFriendStore } from '@/stores/useFriendStore'
import { EditFriendRemarkDialog } from '../dialog';

interface FriendDetailProps {
  friend: FriendInfo
  onNavigateToUserChat?: (userId: string) => Promise<void>
  onFriendRemoved?: () => void
}

export default function FriendDetail({ friend, onNavigateToUserChat, onFriendRemoved }: FriendDetailProps) {
  const [isBlocked, setIsBlocked] = useState(false)
  const [showEditRemarkDialog, setShowEditRemarkDialog] = useState(false)
  const [editDialogInitialFocus, setEditDialogInitialFocus] = useState<'remark' | 'alias'>('remark')
  const friendActions = useFriendActions()
  const { refreshFriendList, addToBlockedList, removeFriendFromList } = useFriendStore()

  // 动态计算显示名称，优先使用别名
  const displayName = friend.alias || friend.nickname

  // 处理拉黑操作
  // @ts-ignore - 忽略 tsc 检查
  const handleBlockToggle = async () => {
    if (isBlocked) {
      // 取消拉黑
      const confirmed = confirm(`确定要取消拉黑用户 ${displayName} 吗？`)
      if (!confirmed) return

      const success = await friendActions.unblockFriend({ friend_id: friend.id })
      if (success) {
        setIsBlocked(false)
        refreshFriendList()
      }
    } else {
      // 拉黑用户
      const confirmed = confirm(`⚠️ 确定要拉黑用户 ${displayName} 吗？\n\n⚠️ 重要提醒：\n• 拉黑后该用户将从您的好友列表中完全移除\n• 您将不会收到此用户的任何消息\n• 此操作可以在设置页面的"拉黑用户列表"中撤销\n\n请确认是否要继续？`)
      if (!confirmed) return

      const success = await friendActions.blockFriend({ friend_id: friend.id })
      if (success) {
        setIsBlocked(true)
        // 将用户添加到拉黑列表并从好友列表移除
        addToBlockedList(friend)
        removeFriendFromList(friend.id)
        onFriendRemoved?.()
      }
    }
  }

  // 处理移除好友
  const handleRemoveFriend = async () => {
    const confirmed = confirm(`确定要移除好友 ${displayName} 吗？`)
    if (!confirmed) return

    const success = await friendActions.removeFriend({ friend_id: friend.id })
    if (success) {
      removeFriendFromList(friend.id)
      onFriendRemoved?.()
    }
  }

  // 处理编辑备注
  const handleEditRemark = () => {
    setEditDialogInitialFocus('remark')
    setShowEditRemarkDialog(true)
  }

  // 处理编辑别名
  const handleEditAlias = () => {
    setEditDialogInitialFocus('alias')
    setShowEditRemarkDialog(true)
  }

  // 处理开始聊天
  const handleStartChat = async () => {
    if (onNavigateToUserChat) {
      await onNavigateToUserChat(friend.id)
    }
  }

  return (
    <div className="bg-gray-50 h-full flex items-center justify-center p-4">
      <div className="w-1/2 mx-auto h-2/3 flex flex-col bg-white rounded-lg shadow-sm py-10">
        {/* 头像和基本信息 */}
        <div className="flex flex-col items-center py-8 px-6">
          <div className="relative mb-4">
            <img
              src={friend.avatar}
              alt={displayName}
              className="w-24 h-24 rounded-full"
            />
          </div>

          <h2 className="text-xl font-semibold text-gray-900 mb-1">
            {displayName}
          </h2>

          <p className="text-sm text-gray-500 mb-4">
            ID: {friend.handle_rand}
          </p>

          {/* 类型标识 */}
          <p className="text-sm text-gray-500 mb-4">
            简介: {friend.bio}
          </p>
        </div>

        {/* 操作按钮区域 */}
        <div className="px-6 space-y-4">
          {/* 备注信息 - 可点击编辑 */}
          {/* <div
            className="flex items-center justify-between py-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50"
            onClick={handleEditRemark}
          >
            <span className="text-sm text-gray-700">备注</span>
            <div className="flex items-center space-x-2">
              <span className={`text-sm max-w-[180px] truncate ${friend.remark ? 'text-gray-600' : 'text-gray-400'
                }`}>
                {friend.remark || '未设置'}
              </span>
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div> */}

          {/* 别名信息 - 可点击编辑 */}
          {/* <div
            className="flex items-center justify-between py-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50"
            onClick={handleEditAlias}
          >
            <span className="text-sm text-gray-700">备注名</span>
            <div className="flex items-center space-x-2">
              <span className={`text-sm max-w-[180px] truncate ${friend.alias ? 'text-gray-600' : 'text-gray-400'
                }`}>
                {friend.alias || '未设置'}
              </span>
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div> */}

          {/* 拉黑功能 */}
          {/* <div
            className="py-3 border-b border-gray-100 cursor-pointer hover:bg-red-50"
            onClick={handleBlockToggle}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-red-600">拉黑用户</div>
                <div className="text-xs text-gray-500">
                  拉黑后该用户将从好友列表中移除，且不会收到此用户的任何消息
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {friendActions.isLoading ? (
                  <div className="text-sm text-gray-400">处理中...</div>
                ) : (
                  <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                )}
              </div>
            </div>
          </div> */}

          {/* 清除聊天记录 */}
          {/* <div className="py-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">清除聊天记录</span>
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div> */}

          {/* 举报功能 */}
          {/* <div
          className="py-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50"
          onClick={handleReport}
        >
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">举报用户</span>
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div> */}

          {/* 移除好友 */}
          {/* <div className="pt-4">
            <button
              onClick={handleRemoveFriend}
              disabled={friendActions.isLoading}
              className="w-full py-2.5 px-4 text-sm font-medium text-red-800 bg-red-50 hover:bg-red-200 border border-red-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {friendActions.isLoading ? '处理中...' : '移除好友'}
            </button>
          </div> */}

          {/* 错误提示 */}
          {friendActions.error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{friendActions.error}</p>
            </div>
          )}
        </div>

        {/* 底部聊天按钮 */}
        <div className="mt-auto p-6">
          <button
            onClick={handleStartChat}
            className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
            disabled={friendActions.isLoading || !onNavigateToUserChat}
          >
            {friendActions.isLoading ? '处理中...' : '开始聊天'}
          </button>
        </div>

        {/* 编辑备注对话框 */}
        <EditFriendRemarkDialog
          open={showEditRemarkDialog}
          onClose={() => setShowEditRemarkDialog(false)}
          friend={friend}
          initialFocus={editDialogInitialFocus}
        />
      </div>
    </div>
  )
}
